import type { DrawerTarget } from '../ObjectDrawer'
import type { RecordContents } from '../RecordTable'
import { humanizeToken } from '../../lib/officerFormat'
import { additionalObjectAccess, type RecordContext } from './context'

/** Existing object-row patterns, kept outside the position table and its totals. */
export default function AdditionalRecordObjects({
  value,
  table,
  asOf,
  evidenced,
  onOpen,
}: {
  value: RecordContext
  table: RecordContents
  asOf: string | null
  evidenced: boolean
  onOpen: (target: DrawerTarget) => void
}) {
  const extra = value.additional_objects
  if (!extra || !['all', 'client'].includes(value.scope.kind)) return null
  if (asOf || extra.coverage === 'historical_membership_unavailable')
    return (
      <p className="nav-muted" data-testid="additional-record-coverage">
        Additional entity and instrument history is unavailable for this date. Open the current
        recorded view to review them.
      </p>
    )
  const entities = extra.entities.filter(
    (row) =>
      row.state !== 'proposed' &&
      !value.records.some((record) =>
        record.record.entities.some((existing) => existing.id === row.id)
      )
  )
  const instruments = extra.instruments.filter(
    (row) =>
      row.state !== 'proposed' &&
      !value.records.some((record) =>
        record.record.instruments.some((existing) => existing.id === row.id)
      )
  )
  const canOpen = (target: DrawerTarget) =>
    !!additionalObjectAccess(value, target, asOf) ||
    value.records.some((record) =>
      target.type === 'entity'
        ? record.record.entities.some((row) => row.id === target.id && row.state !== 'proposed')
        : target.type === 'instrument' &&
          record.record.instruments.some((row) => row.id === target.id && row.state !== 'proposed')
    )
  const name = (label: string | null | undefined, target: DrawerTarget | null) =>
    target && canOpen(target) ? (
      <button type="button" className="nav-link" onClick={() => onOpen(target)}>
        {label ?? 'Name not recorded'}
      </button>
    ) : (
      (label ?? 'Not recorded')
    )
  if (!entities.length && !instruments.length)
    return extra.counts.recorded > 0 && evidenced ? (
      <p className="nav-muted" data-testid="additional-record-filter-empty">
        No additional entities or instruments match the evidenced filter.
      </p>
    ) : null
  return (
    <div data-testid="additional-record-objects">
      {entities.length ? (
        <section className="fa-card">
          <div className="fa-chd">Entities</div>
          <div style={{ overflowX: 'auto' }}>
            <table className="rt-table" aria-label="Additional recorded entities">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Legal form</th>
                  <th>Jurisdiction</th>
                  <th>Registry</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {entities.map((row) => (
                  <tr key={row.id} data-testid="additional-entity-row" data-object-id={row.id}>
                    <td className="rtname">{name(row.name, { type: 'entity', id: row.id })}</td>
                    <td>{row.legal_form ?? '—'}</td>
                    <td>{row.jurisdiction ?? '—'}</td>
                    <td>{row.registry ?? '—'}</td>
                    <td>
                      <span className={`chip rt-${row.state}`}>{row.state}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {instruments.length ? (
        <section className="fa-card">
          <div className="fa-chd">Instruments</div>
          <div style={{ overflowX: 'auto' }}>
            <table className="rt-table" aria-label="Additional recorded instruments">
              <thead>
                <tr>
                  <th>Instrument</th>
                  <th>Issuer</th>
                  <th>Category</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {instruments.map((row) => (
                  <tr key={row.id} data-testid="additional-instrument-row" data-object-id={row.id}>
                    <td className="rtname">{name(row.name, { type: 'instrument', id: row.id })}</td>
                    <td>
                      {name(
                        row.issuer_name,
                        row.issuer_id &&
                          table.entities.some((entity) => entity.id === row.issuer_id)
                          ? { type: 'entity', id: row.issuer_id }
                          : null
                      )}
                    </td>
                    <td>{humanizeToken(row.category)}</td>
                    <td>
                      <span className={`chip rt-${row.state}`}>{row.state}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  )
}
